import { useTranslation, Trans } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { DynamicFieldType } from '@/api/types';
import type { DraftField, WizardDraft } from './state';
import { validateFields } from './state';

interface Props {
  draft: WizardDraft;
  onChange: (patch: Partial<WizardDraft>) => void;
}

const PRIMITIVES: DynamicFieldType[] = ['string', 'integer', 'number', 'boolean'];

let nextFieldId = 0;
function newField(): DraftField {
  nextFieldId += 1;
  return {
    id: `new-${Date.now()}-${nextFieldId}`,
    name: '',
    type: 'string',
    itemsType: 'string',
    description: '',
    required: true,
    enumValues: '',
  };
}

export function Step3Inputs({ draft, onChange }: Props): JSX.Element {
  const { t } = useTranslation();
  const errors = validateFields(draft).errors;

  const updateField = (id: string, patch: Partial<DraftField>): void => {
    onChange({
      fields: draft.fields.map((f) => (f.id === id ? { ...f, ...patch } : f)),
    });
  };

  const removeField = (id: string): void => {
    onChange({ fields: draft.fields.filter((f) => f.id !== id) });
  };

  const addField = (): void => {
    onChange({ fields: [...draft.fields, newField()] });
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <Trans i18nKey="tools.wizard.step3.intro" components={{ code: <code /> }} />
      </p>

      {draft.fields.length === 0 ? (
        <p className="rounded border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
          {t('tools.wizard.step3.empty')}
        </p>
      ) : (
        <div className="space-y-2">
          {draft.fields.map((field) => (
            <FieldRow
              key={field.id}
              field={field}
              onChange={(p) => updateField(field.id, p)}
              onRemove={() => removeField(field.id)}
            />
          ))}
        </div>
      )}

      {errors.fields ? <p className="text-[11px] text-destructive">{errors.fields}</p> : null}

      <Button type="button" variant="outline" size="sm" onClick={addField} className="gap-1.5">
        <Plus className="h-3 w-3" />
        {t('tools.wizard.step3.add')}
      </Button>
    </div>
  );
}

function FieldRow({
  field,
  onChange,
  onRemove,
}: {
  field: DraftField;
  onChange: (patch: Partial<DraftField>) => void;
  onRemove: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-12 items-start gap-2 rounded border bg-muted/20 p-2">
      <div className="col-span-3 space-y-1">
        <span className="text-[10px] uppercase text-muted-foreground">{t('tools.wizard.step3.name')}</span>
        <Input
          value={field.name}
          onChange={(e) => onChange({ name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
          placeholder={t('tools.wizard.step3.namePlaceholder')}
          className="font-mono text-xs"
        />
      </div>
      <div className="col-span-2 space-y-1">
        <span className="text-[10px] uppercase text-muted-foreground">{t('tools.wizard.step3.type')}</span>
        <Select
          value={field.type}
          onValueChange={(v) => onChange({ type: v as DraftField['type'] })}
        >
          <SelectTrigger className="h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRIMITIVES.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
            <SelectItem value="array">array</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {field.type === 'array' ? (
        <div className="col-span-2 space-y-1">
          <span className="text-[10px] uppercase text-muted-foreground">{t('tools.wizard.step3.itemType')}</span>
          <Select
            value={field.itemsType}
            onValueChange={(v) => onChange({ itemsType: v as DraftField['itemsType'] })}
          >
            <SelectTrigger className="h-9 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRIMITIVES.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <div className={field.type === 'array' ? 'col-span-4 space-y-1' : 'col-span-6 space-y-1'}>
        <span className="text-[10px] uppercase text-muted-foreground">{t('tools.wizard.step3.description')}</span>
        <Input
          value={field.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder={t('tools.wizard.step3.descriptionPlaceholder')}
          className="text-xs"
        />
      </div>
      <div className="col-span-1 flex flex-col items-center pt-5 text-xs">
        <Checkbox
          checked={field.required}
          onCheckedChange={(v) => onChange({ required: v === true })}
          aria-label={t('tools.wizard.step3.requiredAria')}
        />
        <span className="mt-1 text-[10px] uppercase text-muted-foreground">{t('tools.wizard.step3.required')}</span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="col-span-12 flex justify-end text-xs text-destructive hover:underline"
      >
        <Trash2 className="mr-1 h-3 w-3" />
        {t('tools.wizard.step3.remove')}
      </button>
      {field.type === 'string' ? (
        <div className="col-span-12 space-y-1">
          <span className="text-[10px] uppercase text-muted-foreground">{t('tools.wizard.step3.enum')}</span>
          <Input
            value={field.enumValues}
            onChange={(e) => onChange({ enumValues: e.target.value })}
            placeholder={t('tools.wizard.step3.enumPlaceholder')}
            className="text-xs"
          />
        </div>
      ) : null}
      {field.type === 'integer' || field.type === 'number' ? (
        <div className="col-span-12 grid grid-cols-2 gap-2">
          <div className="space-y-1">
            <span className="text-[10px] uppercase text-muted-foreground">{t('tools.wizard.step3.min')}</span>
            <Input
              type="number"
              value={field.minimum ?? ''}
              onChange={(e) =>
                onChange({ minimum: e.target.value === '' ? undefined : Number(e.target.value) })
              }
              className="text-xs"
            />
          </div>
          <div className="space-y-1">
            <span className="text-[10px] uppercase text-muted-foreground">{t('tools.wizard.step3.max')}</span>
            <Input
              type="number"
              value={field.maximum ?? ''}
              onChange={(e) =>
                onChange({ maximum: e.target.value === '' ? undefined : Number(e.target.value) })
              }
              className="text-xs"
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
